import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { finalize, flatMap } from 'rxjs/operators';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { ServiceordreDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-dto';
import { OpgaveEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/opgave-enum';
import { ServiceordreStatusEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceordre-status-enum';
import { HandleKreditstopRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/handle-kreditstop-request';
import { HandleValidationFailedRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/handle-validation-failed-request';
import { KreditvurderingService, ServiceordreService } from 'src/app/api/services';
import { KreditvaerdigEnumDto } from '../api/models/OK/Egis/Service/Models/DTO/kreditvaerdig-enum-dto';
import { KreditvurderingDto } from '../api/models/OK/Egis/Service/Models/DTO/kreditvurdering-dto';
import { KreditstopDialogComponent } from '../component/ordre/kreditstop-dialog/kreditstop-dialog.component';
import { KreditvurderingDialogComponent } from '../component/ordre/kreditvurdering-dialog/kreditvurdering-dialog.component';
import { ValidationFailedDialogComponent } from '../component/ordre/validation-failed-dialog/validation-failed-dialog.component';
import {
  CreateServiceOrdreComponentDialogData,
  KreditstopDialogComponentData,
  KreditstopDialogResponse,
} from '../components/aftale/models';
import { CreateEftersynComponent } from '../components/ordre/eftersyn/create-eftersyn/create-eftersyn.component';
import { CreateTilkaldComponent } from '../components/ordre/tilkald/create/create-tilkald.component';
import { EGIS_SERVICE_CALLER } from '../constants';
import { LoaderService } from './loader.service';

@Injectable({
  providedIn: 'root',
})
export class OrdreService {
  serviceordre$ = new Subject<ServiceordreDto>();
  private kreditvurdering: KreditvurderingDto;

  constructor(
    private kreditVurderingService: KreditvurderingService,
    private dialog: MatDialog,
    private serviceordreService: ServiceordreService,
    private loader: LoaderService,
  ) {}

  create(type: OpgaveEnum, serviceaftale: ServiceaftaleDto, serviceordre: ServiceordreDto): Observable<ServiceordreDto> {
    this.loader.show();
    this.kreditVurderingService
      .getKreditVurderingForServiceordre({
        caller: EGIS_SERVICE_CALLER,
        body: {
          okKundeAftaleNr: serviceaftale.okKundeAftaleNr,
          opgave: type,
        },
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
      )
      .subscribe((kreditvurdering: KreditvurderingDto) => {
        const data: CreateServiceOrdreComponentDialogData = {
          serviceaftale: serviceaftale,
          serviceordre: serviceordre,
          kreditvurdering: kreditvurdering,
          opgavetype: type,
        };

        var ordreComponent: any = null;
        {
          switch (type) {
            case OpgaveEnum.Tilkald:
              ordreComponent = CreateTilkaldComponent;
              break;
            case OpgaveEnum.Opstart:
              ordreComponent = CreateTilkaldComponent;
              break;
            default:
              ordreComponent = CreateEftersynComponent;
              break;
          }
        }

        if (
          kreditvurdering?.kreditvaerdig === KreditvaerdigEnumDto.No ||
          kreditvurdering?.kreditvaerdig === KreditvaerdigEnumDto.Maybe ||
          kreditvurdering?.kreditvaerdig === KreditvaerdigEnumDto.NotSet
        ) {
          const dialogRef = this.dialog.open(KreditvurderingDialogComponent, {
            width: '600px',
            maxWidth: '95vw',
            data: data,
            disableClose: true,
            hasBackdrop: true,
          });
          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              const tilkaldDialogRef = this.dialog.open(ordreComponent, {
                width: '600px',
                maxWidth: '95vw',
                data: data,
                disableClose: true,
                hasBackdrop: true,
              });
              tilkaldDialogRef.afterClosed().subscribe((serviceordre: ServiceordreDto) => {
                if (serviceordre) this.serviceordre$.next(serviceordre);
              });
            }
          });
        } else {
          const tilkaldDialogRef = this.dialog.open(ordreComponent, {
            width: '600px',
            maxWidth: '95vw',
            data: data,
            disableClose: true,
            hasBackdrop: true,
          });
          tilkaldDialogRef.afterClosed().subscribe((serviceordre: ServiceordreDto) => {
            if (serviceordre) this.serviceordre$.next(serviceordre);
          });
        }
      });

    return this.serviceordre$;
  }

  kreditstop$(serviceordre: ServiceordreDto): Observable<any> {
    this.loader.show();
    return this.kreditVurderingService
      .getKreditVurderingForServiceordre({
        caller: EGIS_SERVICE_CALLER,
        body: {
          okKundeAftaleNr: serviceordre.okKundeAftalenr,
          opgave: serviceordre.serviceordreType,
        },
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
      )
      .pipe(
        flatMap((kreditvurdering: KreditvurderingDto) => {
          this.kreditvurdering = kreditvurdering;
          let data: KreditstopDialogComponentData = {
            serviceordre: serviceordre,
            kreditvurdering: kreditvurdering,
            partnervalidTo: serviceordre.partner?.validTo,
          };
          let dialogRef = this.dialog.open(KreditstopDialogComponent, {
            width: '600px',
            maxWidth: '95vw',
            data: data,
          });

          return dialogRef.afterClosed();
        }),
        flatMap((result: KreditstopDialogResponse) => {
          if (!result) {
            return of(null);
          }
          const request: HandleKreditstopRequest = {
            serviceordreNo: serviceordre.serviceordreNo,
            serviceordreStatus: result.serviceordreStatus,
            kreditadvarselAccept: {
              acceptTekst: result.acceptTekst,
              okKundeAftalenr: serviceordre.okKundeAftalenr,
              aarsagTekst: this.kreditvurdering.aarsagTekst,
              internalG4KreditkodeId: this.kreditvurdering.internalG4KreditkodeId,
            },
          };
          return this.serviceordreService.handleKreditstop({ caller: EGIS_SERVICE_CALLER, body: request });
        }),
      );
  }

  handleValidationFailed$(serviceordre: ServiceordreDto): Observable<void> {
    let dialogRef = this.dialog.open(ValidationFailedDialogComponent, {
      data: serviceordre,
    });

    return dialogRef.afterClosed().pipe(
      flatMap((result: ServiceordreStatusEnum) => {
        if (!result) {
          return of(null);
        }
        const request: HandleValidationFailedRequest = {
          serviceordreNo: serviceordre.serviceordreNo,
          serviceordreStatus: result,
        };
        return this.serviceordreService.handleValidationFailed({ caller: EGIS_SERVICE_CALLER, body: request });
      }),
    );
  }
}
